import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { ImageViewer } from '../focus/ImageViewer';
import { SearchRangeBar } from './SearchRangeBar';
import type { ImageLabel, ToolImage } from '../../types/LabelingTypes';

type ComparisonVariant = 'compare' | 'verify';

interface VariantCopy {
  title: string;
  progress: string;
  /** Explanatory sentence shown above the image — only the verification step has one. */
  intro: string | null;
  question: string;
}

const VARIANT_COPY: Record<ComparisonVariant, VariantCopy> = {
  compare: {
    title: 'labeling.dichotomous.compare.title',
    progress: 'labeling.dichotomous.compare.progress',
    intro: null,
    question: 'labeling.dichotomous.compare.question',
  },
  verify: {
    title: 'labeling.dichotomous.verify.title',
    progress: 'labeling.dichotomous.verify.progress',
    intro: 'labeling.dichotomous.verify.intro',
    question: 'labeling.dichotomous.verify.question',
  },
};

interface AnswerConfig {
  state: ImageLabel;
  labelKey: string;
  shortcut: string;
  /** Full literal class strings so Tailwind can statically pick the tokens up. */
  tone: string;
  dot: string;
  /**
   * Way out rather than a verdict: rendered as a single low line under the two answers, so it
   * never competes with them for the annotator's eye.
   */
  secondary?: boolean;
}

const ANSWERS: AnswerConfig[] = [
  {
    state: 'ok',
    labelKey: 'labeling.dichotomous.compare.btnOk',
    shortcut: '1',
    tone: 'border-ok-ring hover:bg-ok-soft hover:text-ok',
    dot: 'bg-ok',
  },
  {
    state: 'worn',
    labelKey: 'labeling.dichotomous.compare.btnWorn',
    shortcut: '2',
    tone: 'border-worn-ring hover:bg-worn-soft hover:text-worn',
    dot: 'bg-worn',
  },
  {
    state: 'unreadable',
    labelKey: 'labeling.dichotomous.compare.btnUnreadable',
    shortcut: '3',
    tone: 'border-unreadable-ring hover:bg-unreadable-soft hover:text-unreadable',
    dot: 'bg-unreadable',
    secondary: true,
  },
];

interface AnswerButtonProps {
  config: AnswerConfig;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

/**
 * Deliberately never renders an "active" state: unlike `LabelPanel`'s `StateButton` this is a
 * one-shot answer, not a toggle — the screen moves on as soon as it is pressed.
 */
function AnswerButton({ config, label, disabled, onClick }: AnswerButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={`dichotomous-answer-${config.state}`}
      className={cn(
        'flex items-center justify-center gap-2 rounded-xl border bg-card transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        config.secondary
          ? 'col-span-2 flex-row px-6 py-3 text-sm font-medium text-muted-foreground'
          : 'flex-col px-6 py-7 text-base font-semibold text-foreground',
        'disabled:pointer-events-none disabled:opacity-50',
        config.tone
      )}
    >
      <span className={cn('h-3 w-3 shrink-0 rounded-full', config.dot)} />
      <span>{label}</span>
      <kbd className="rounded border border-border/50 bg-muted/60 px-1.5 py-0.5 font-mono text-2xs">
        {config.shortcut}
      </kbd>
    </button>
  );
}

interface ComparisonScreenProps {
  /** `compare` drives the binary search; `verify` re-checks the converged breakpoint. */
  variant: ComparisonVariant;
  /** Image the annotator has to judge. */
  image: ToolImage;
  /** Position of `image` in the chronological sequence, shown by the viewer footer. */
  position: { index: number; total: number };
  /** Question counter, e.g. "comparison 2 of ~4". */
  progress: { n: number; total: number };
  /** Search bracket rendered by `SearchRangeBar` — compare variant only. */
  bracket: { lo: number; hi: number; total: number; current: number | null };
  onAnswer(label: ImageLabel): void;
  /** Blocks every answer while a transition is in flight. */
  disabled?: boolean;
}

/**
 * The single question screen of the dichotomous mode: one image, two answers — plus a way out
 * for an image that cannot be judged, which the search then sets aside. Both the search
 * comparisons and the post-search verification use it — they differ only in copy and in whether
 * the search bracket is shown. Reads no labeling context of its own.
 */
export function ComparisonScreen({
  variant,
  image,
  position,
  progress,
  bracket,
  onAnswer,
  disabled = false,
}: ComparisonScreenProps) {
  const { t } = useTranslation();
  const copy = VARIANT_COPY[variant];

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="section-title">{t(copy.title)}</h2>
          <span className="font-mono text-xs text-muted-foreground">
            {t(copy.progress, { n: progress.n, total: progress.total })}
          </span>
        </div>
        {copy.intro && <p className="text-sm text-muted-foreground">{t(copy.intro)}</p>}
        {variant === 'compare' && (
          <SearchRangeBar
            total={bracket.total}
            lo={bracket.lo}
            hi={bracket.hi}
            current={bracket.current}
          />
        )}
      </div>

      <ImageViewer image={image} position={position} showLabelChip={false} />

      <div className="flex flex-col gap-3">
        <p className="text-center text-sm font-medium text-foreground">{t(copy.question)}</p>
        <div className="grid grid-cols-2 gap-4">
          {ANSWERS.map((config) => (
            <AnswerButton
              key={config.state}
              config={config}
              label={t(config.labelKey)}
              disabled={disabled}
              onClick={() => onAnswer(config.state)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
